import * as React from 'react';
import { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import MuiDrawer, { drawerClasses } from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip'; // --- MODIFICATION: Imported Chip for the badge ---
import { useNavigate } from 'react-router-dom';
import MenuContent from './MenuContent';
import OptionsMenu from './OptionsMenu';
import { authFetch } from '../authFetch';
import { useSimulation } from './contexts/SimulationContext';

const drawerWidth = 240;
const logo = '/logo.svg';

const Drawer = styled(MuiDrawer)({
  width: drawerWidth,
  flexShrink: 0,
  boxSizing: 'border-box',
  mt: 10,
  [`& .${drawerClasses.paper}`]: {
    width: drawerWidth,
    boxSizing: 'border-box',
  },
});

export default function SideMenu() {
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState({
    username: '',
    email: '',
    profile_picture: null,
  });

  const { promptNavigation } = useSimulation();

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await authFetch('/api/user/profile/');
        const data = await response.json();
        setUserProfile(data);
      } catch (error) {
        console.error("Failed to fetch user profile for side menu:", error);
      }
    };

    fetchProfile();

    window.addEventListener('profileUpdated', fetchProfile);
    return () => window.removeEventListener('profileUpdated', fetchProfile);
  },[]);

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    navigate('/signin');
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        display: { xs: 'none', md: 'block' },
        [`& .${drawerClasses.paper}`]: {
          backgroundColor: 'background.paper',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center', // Vertically centers the logo and the badge
          mt: 'calc(var(--template-frame-height, 0px) + 4px)',
          p: 1.5,
        }}
      >
        <img src={logo} alt="Logo" style={{ height: '40px', paddingLeft: '11px' }} />
        
        {/* --- MODIFICATION: Added Beta Badge --- */}
        <Chip
          label="BETA"
          size="small"
          sx={{
            ml: 1.5,
            height: 20,
            fontSize: '0.65rem',
            fontWeight: 'bold',
            letterSpacing: '0.5px',
            backgroundColor: 'action.selected', // Uses MUI's default subtle gray
            color: 'text.secondary', // Dark gray text
            cursor: 'default',
          }}
        />
      </Box>
      <Divider />
      <Box
        sx={{
          overflow: 'auto',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <MenuContent onNavigate={promptNavigation} />
      </Box>
      <Stack
        direction="row"
        sx={{
          p: 2,
          gap: 1,
          alignItems: 'center',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Avatar
          sizes="small"
          alt={userProfile.username}
          src={userProfile.profile_picture}
          sx={{ width: 36, height: 36 }}
        >
          {userProfile.username && userProfile.username.charAt(0).toUpperCase()}
        </Avatar>
        <Box sx={{ mr: 'auto', minWidth: 0 }}>
          <Typography
            noWrap
            variant="body2"
            sx={{
              fontWeight: 500,
              lineHeight: '16px',
            }}
          >
            {userProfile.username || 'Loading...'}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {userProfile.email}
          </Typography>
        </Box>
        <OptionsMenu onLogout={handleLogout} />
      </Stack>
    </Drawer>
  );
}